import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { FaChevronDown, FaPlus } from 'react-icons/fa';
import { kbBuilderService } from '../../../services/kbBuilder.service';
import AIBuilderChatPanel from './AIBuilderChatPanel';
import coryImg from './cory.jpeg';
import './AIBuilderWorkspace.css'; // shared .ai-chat-*/.ai-bubble-* styles
import './KbBuilderBubble.css';

const REQUEST_TIMEOUT_MS = 60000;
const DRAG_MARGIN = 8; // keep the window at least this far from each viewport edge
const DRAG_THRESHOLD = 4; // px of movement before a press counts as a drag (vs a click)
const FAB_SIZE = 66; // must match .kb-builder-fab width/height
const PANEL_W = 440; // must match .kb-builder-panel's clamp() ceiling width
const PANEL_H = 560; // must match .kb-builder-panel's clamp() ceiling height
const MIN_W = 320; // minimum panel width when resizing — must match the clamp() floor
const MIN_H = 340; // minimum panel height when resizing — must match the clamp() floor

const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

// Mirrors .kb-builder-panel's CSS `clamp(MIN, vw/vh %, PANEL)` sizing in JS, so a position
// computed before the panel has ever rendered (nothing to measure yet) still matches what the
// CSS actually renders instead of assuming the fixed ceiling size unconditionally.
const defaultPanelSize = () => ({
  w: clamp(window.innerWidth * 0.32, MIN_W, PANEL_W),
  h: clamp(window.innerHeight * 0.45, MIN_H, PANEL_H),
});

// Decode a base64 payload (the issue-report zip) into a Blob for download.
function base64ToBlob(base64, type) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: type || 'application/octet-stream' });
}

// Given the icon's top-left, compute where the expanded panel's top-left should go so it
// "explodes" toward the open space — up-and-left when the icon is tucked bottom-right (the
// common case), so a wide panel never overflows off-screen. Returns null when the icon is at
// its default corner (panel then uses the CSS default bottom-right anchor).
function explodedPanelPos(iconPos, size) {
  if (!iconPos) return null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const fallback = defaultPanelSize();
  const w = Math.min(size?.w || fallback.w, vw - 2 * DRAG_MARGIN);
  const h = Math.min(size?.h || fallback.h, vh - 2 * DRAG_MARGIN);
  const expandLeft = iconPos.x + FAB_SIZE / 2 > vw / 2;
  const expandUp = iconPos.y + FAB_SIZE / 2 > vh / 2;
  const x = expandLeft ? iconPos.x + FAB_SIZE - w : iconPos.x;
  const y = expandUp ? iconPos.y + FAB_SIZE - h : iconPos.y;
  return {
    x: clamp(x, DRAG_MARGIN, vw - w - DRAG_MARGIN),
    y: clamp(y, DRAG_MARGIN, vh - h - DRAG_MARGIN),
  };
}

// CoryBot — a draggable floating window over the workflow builder. Fixed-position so it
// never consumes canvas space; drag it anywhere by its header (or the minimized bubble) and
// minimize back to that bubble. Independent from the inline AIBuilderWorkspace: its own
// open/message/in-flight state and its own service (kb-generate). The server owns the
// transcript keyed by conversationId, so once we have one we stop sending prior messages and
// just echo the id. The canvas remains the source of truth for the runbook, so we send the
// current canvas JSON every turn and only replace it on kind === 'runbook'.
function KbBuilderBubble({ clientId, clientGuid, getCurrentRunbookJson, onHydrate, onInFlightChange, onSetRunbookMeta }) {
  const [open, setOpen] = useState(false); // starts minimized to the bubble
  const [messages, setMessages] = useState([]);
  const [inFlight, setInFlight] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  // Two independent positions (top-left, viewport px; null => default corner via CSS): the
  // minimized bubble and the open panel. Keeping them separate lets the bubble stay where you
  // left it while the panel explodes toward open space on open.
  const [iconPos, setIconPos] = useState(null);
  const [panelPos, setPanelPos] = useState(null);
  const [size, setSize] = useState(null); // {w, h} once resized; null => CSS default
  const windowRef = useRef(null);
  const dragOffset = useRef(null);
  const movedRef = useRef(false); // did the last press move far enough to be a drag?

  useEffect(() => {
    if (onInFlightChange) onInFlightChange(inFlight);
  }, [inFlight, onInFlightChange]);

  // Drag the currently-shown element (panel header when open, bubble when minimized) by its
  // top-left. Clamps within the viewport. Sets movedRef so a click can be told from a drag.
  // Only commits a position once real movement crosses DRAG_THRESHOLD — a plain click (the
  // common case) never sets iconPos/panelPos at all, leaving them at their default `null` so
  // the panel opens via the CSS's own bottom-right anchor (the same corner the bubble was in)
  // instead of the "explode toward open space" math below, which assumes a fixed panel size
  // and can misplace the panel once it no longer matches the CSS's actual (responsive) size.
  const handleDragStart = useCallback((e) => {
    if (e.button !== 0 || !windowRef.current) return;
    const setPosition = open ? setPanelPos : setIconPos;
    const rect = windowRef.current.getBoundingClientRect();
    dragOffset.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    const startX = e.clientX;
    const startY = e.clientY;
    movedRef.current = false;
    e.preventDefault();

    const onMove = (ev) => {
      const off = dragOffset.current;
      const el = windowRef.current;
      if (!off || !el) return;
      if (!movedRef.current && Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) > DRAG_THRESHOLD) {
        movedRef.current = true;
      }
      if (!movedRef.current) return;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const x = clamp(ev.clientX - off.dx, DRAG_MARGIN, window.innerWidth - w - DRAG_MARGIN);
      const y = clamp(ev.clientY - off.dy, DRAG_MARGIN, window.innerHeight - h - DRAG_MARGIN);
      setPosition({ x, y });
    };
    const onUp = () => {
      dragOffset.current = null;
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [open]);

  // Open from the bubble: explode the panel toward open space so it never renders off-screen.
  const openPanel = useCallback(() => {
    setPanelPos(explodedPanelPos(iconPos, size));
    setOpen(true);
  }, [iconPos, size]);

  // Resize from the top-left grip, keeping the panel's bottom-right corner fixed so it grows
  // up-and-left (matching its default bottom-right anchor). Uses explicit left/top + width/height
  // so it behaves the same whether or not the panel has been dragged.
  const handleResizeStart = useCallback((e) => {
    if (e.button !== 0 || !windowRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = windowRef.current.getBoundingClientRect();
    const brX = rect.right;
    const brY = rect.bottom;

    const onMove = (ev) => {
      const w = clamp(brX - ev.clientX, MIN_W, brX - DRAG_MARGIN);
      const h = clamp(brY - ev.clientY, MIN_H, brY - DRAG_MARGIN);
      setSize({ w, h });
      setPanelPos({ x: brX - w, y: brY - h });
    };
    const onUp = () => {
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const iconStyle = iconPos ? { left: iconPos.x, top: iconPos.y, right: 'auto', bottom: 'auto' } : undefined;
  const panelStyle = (panelPos || size)
    ? {
        ...(panelPos ? { left: panelPos.x, top: panelPos.y, right: 'auto', bottom: 'auto' } : {}),
        ...(size ? { width: size.w, height: size.h } : {}),
      }
    : undefined;

  // Fresh canvas state on every send. Empty canvas (start node only, no real
  // steps) yields null so the assistant treats the prompt as a fresh build.
  const buildCurrentRunbookPayload = useCallback(() => {
    if (typeof getCurrentRunbookJson !== 'function') return null;
    try {
      const json = getCurrentRunbookJson();
      if (!json || !Array.isArray(json.steps) || json.steps.length === 0) return null;
      return json;
    } catch {
      return null;
    }
  }, [getCurrentRunbookJson]);

  const startNewConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
  }, []);

  const send = useCallback(async (prompt, sampleFiles) => {
    if (!clientId || !clientGuid) {
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: prompt },
        { role: 'assistant', kind: 'error', content: 'Select a client before using CoryBot.' },
      ]);
      return;
    }

    const userBubble = { role: 'user', content: prompt };
    setMessages((prev) => [...prev, userBubble]);
    setInFlight(true);

    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error('Generation took too long — try again or simplify the request.')),
        REQUEST_TIMEOUT_MS
      );
    });

    try {
      // Omit conversationId on the first turn; echo it thereafter. The server owns
      // history once a conversationId exists, so we never send prior `messages`.
      const payload = {
        clientId,
        clientGuid,
        prompt,
        currentRunbook: buildCurrentRunbookPayload(),
        clientNotes: [],
      };
      // NOTE: the logged-in user is NOT sent from here — the Web API stamps it server-side
      // from the auth claim (trustworthy, not spoofable) and records it as the conversation
      // creator for usage analytics.
      if (conversationId) payload.conversationId = conversationId;
      // Optional sample files for structure inference — sent only on the turn the user
      // attaches them; the server remembers what it derived, so we don't resend.
      if (sampleFiles && sampleFiles.length > 0) payload.sampleFiles = sampleFiles;

      const data = await Promise.race([kbBuilderService.generate(payload), timeoutPromise]);
      clearTimeout(timeoutId);

      // conversationId is returned on every kind (including error) — keep the thread alive.
      if (data.conversationId) setConversationId(data.conversationId);

      const assistantText = data.assistantMessage || data.message || '';

      if (data.kind === 'runbook') {
        setMessages((prev) => [...prev, { role: 'assistant', kind: 'runbook', content: assistantText }]);
        // Always hydrate on a runbook turn so the canvas reflects the model's latest output.
        // (Non-runbook turns — needs_input/message/refusal — never reach here, so the canvas is
        // untouched during the question phase.)
        if (data.runbookJson && onHydrate) onHydrate(data.runbookJson);
        if (onSetRunbookMeta) {
          onSetRunbookMeta({ name: data.runbookName, description: data.runbookDescription });
        }
      } else if (data.kind === 'message') {
        // Pure conversational reply — render as a bubble, do NOT touch the canvas.
        setMessages((prev) => [...prev, { role: 'assistant', kind: 'message', content: assistantText }]);
      } else if (data.kind === 'needs_input') {
        setMessages((prev) => [...prev, { role: 'assistant', kind: 'needs_input', content: assistantText }]);
      } else if (data.kind === 'refusal') {
        if (data.reason) {
          // eslint-disable-next-line no-console
          console.debug('[KbBuilder] refusal reason:', data.reason);
        }
        setMessages((prev) => [...prev, { role: 'assistant', kind: 'refusal', content: assistantText }]);
      } else {
        // Includes kind === 'error' (rate cap, model error) — render as a soft error.
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', kind: 'error', content: assistantText || 'Unexpected response from CoryBot.' },
        ]);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      const serverMessage = err?.response?.data?.message;
      const errText = serverMessage || err?.message || 'Something went wrong. Try again.';
      setMessages((prev) => [...prev, { role: 'assistant', kind: 'error', content: errText }]);
    } finally {
      setInFlight(false);
    }
  }, [clientId, clientGuid, conversationId, buildCurrentRunbookPayload, onHydrate, onSetRunbookMeta]);

  // Report an issue: ask the server to bundle this conversation (transcript, decisions,
  // metadata) + the current canvas runbook into a zip, then download it. Only meaningful once a
  // conversation exists.
  const reportIssue = useCallback(async () => {
    if (!conversationId || reporting) return;
    setReporting(true);
    try {
      const data = await kbBuilderService.reportIssue({
        conversationId,
        currentRunbook: buildCurrentRunbookPayload(),
      });
      if (data?.contentBase64) {
        const blob = base64ToBlob(data.contentBase64, data.contentType);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.fileName || 'ai-builder-issue.zip';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', kind: 'error', content: 'Could not build the issue report — try again.' },
      ]);
    } finally {
      setReporting(false);
    }
  }, [conversationId, reporting, buildCurrentRunbookPayload]);

  if (!open) {
    return (
      <button
        type="button"
        ref={windowRef}
        className="kb-builder-fab"
        style={iconStyle}
        onMouseDown={handleDragStart}
        onClick={() => {
          // Suppress the open if the press was actually a drag.
          if (movedRef.current) { movedRef.current = false; return; }
          openPanel();
        }}
        title="CoryBot — click to open, drag to move"
        aria-label="Open CoryBot"
      >
        <img src={coryImg} alt="Cory" className="kb-builder-fab-img" draggable={false} />
      </button>
    );
  }

  return (
    <div className="kb-builder-panel" ref={windowRef} style={panelStyle} role="dialog" aria-label="CoryBot">
      <div className="kb-resize-handle" onMouseDown={handleResizeStart} title="Drag to resize" aria-hidden="true" />
      <div className="ai-builder-header kb-builder-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center kb-drag-handle" onMouseDown={handleDragStart} title="Drag to move">
          <img src={coryImg} alt="Cory" className="kb-builder-avatar me-2" draggable={false} />
          <strong>CoryBot</strong>
        </div>
        <div className="d-flex align-items-center">
          <Button
            variant="link"
            size="sm"
            onClick={startNewConversation}
            className="ai-builder-close p-0 me-3"
            title="New conversation"
            aria-label="New conversation"
          >
            <FaPlus />
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={() => setOpen(false)}
            className="ai-builder-close p-0"
            title="Minimize — keeps this conversation"
            aria-label="Minimize CoryBot"
          >
            <FaChevronDown />
          </Button>
        </div>
      </div>
      <AIBuilderChatPanel
        messages={messages}
        inFlight={inFlight}
        onSend={send}
        allowSampleFiles
        onReportIssue={reportIssue}
        reportDisabled={!conversationId || reporting || inFlight}
      />
    </div>
  );
}

export default KbBuilderBubble;
